import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Key, ShieldAlert, Sparkles, Image, Music, Film, Coins, CreditCard, ShieldCheck, Loader2, Eye, EyeOff } from "lucide-react";
import { useSession } from "@/context/SessionContext";
import { vaultSecurityApi } from "@/api/vaultSecurity";
import PlansPanel from "@/components/billing/PlansPanel";

// Wallet & Monetization Hub — Tier & Keys panel (S20).
// Real hooks: wallet.balance (credits) + encrypted BYOK OPENAI_API_KEY.
// DISPLAY-ONLY: pro monthly inclusions (no per-category counters yet), the
// "Day 1 Taste" badge, and the Director guard demo (real generation charges
// happen inside each studio via require_credits).
const OPENAI_KEY_NAME = "OPENAI_API_KEY";

const INCLUSIONS = [
  { key: "images", label: "Images", Icon: Image, color: "#00E5FF", max: 20 },
  { key: "music", label: "Music", Icon: Music, color: "#A855F7", max: 5 },
  { key: "director", label: "Director", Icon: Film, color: "#F87171", max: 5 },
];

export default function TierKeyPanel() {
  const { user, wallet } = useSession();
  const [keyInput, setKeyInput] = useState("");
  const [keySet, setKeySet] = useState(false);
  const [keyMasked, setKeyMasked] = useState(null);
  const [show, setShow] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showPlans, setShowPlans] = useState(false);

  const isCasual = (user?.subscription_tier || "tier_1") === "tier_1";
  const isPro = ["tier_2", "tier_3"].includes(user?.subscription_tier);
  const availableCredits = Number(wallet?.balance ?? user?.meta?.milink ?? 0);
  const isFirstTasteActive = isCasual && availableCredits > 0 && availableCredits <= 10;

  const loadKey = async () => {
    try {
      const keys = await vaultSecurityApi.byokRegistry();
      const k = (keys || []).find((x) => x.name === OPENAI_KEY_NAME);
      setKeySet(!!k?.set);
      setKeyMasked(k?.masked || null);
    } catch { /* not critical */ }
  };
  useEffect(() => { loadKey(); }, []);

  const saveKey = async () => {
    const v = keyInput.trim();
    if (v.length < 8) { toast.error("Enter a valid OpenAI key"); return; }
    setSaving(true);
    try {
      await vaultSecurityApi.byokRegSet(OPENAI_KEY_NAME, v);
      setKeyInput("");
      await loadKey();
      toast.success("OpenAI key saved securely");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save key"); }
    finally { setSaving(false); }
  };

  const wipeKey = async () => {
    setSaving(true);
    try { await vaultSecurityApi.byokRegWipe(OPENAI_KEY_NAME); await loadKey(); toast("OpenAI key removed"); }
    catch { toast.error("Could not remove key"); }
    finally { setSaving(false); }
  };

  const demoGuard = () =>
    toast("Illustrative — real generation runs inside each studio and only charges credits on success.", { icon: "🛡️" });

  return (
    <div className="w-full text-white select-none" data-testid="tier-key-panel">
      {/* Pay-As-You-Go ledger (casual / tier_1) */}
      {isCasual && (
        <div className="w-full bg-[#0A0A0A] border border-zinc-900 rounded-xl p-4 mb-4" data-testid="payg-ledger">
          <div className="flex justify-between items-center mb-3">
            <div className="flex items-center gap-1.5">
              <Coins className="w-4 h-4 text-[#FFCC00]" />
              <span className="text-[10px] text-zinc-400 font-black tracking-widest uppercase">Pay-As-You-Go System</span>
            </div>
            {isFirstTasteActive && (
              <span className="text-[8px] bg-emerald-950 text-emerald-400 border border-emerald-900 px-2 py-0.5 rounded font-black tracking-wide uppercase animate-pulse" data-testid="payg-taste-badge">
                Day 1 Taste Active
              </span>
            )}
          </div>
          <div className="flex justify-between items-center bg-black/40 border border-zinc-800 p-3 rounded-lg mb-3">
            <div>
              <h5 className="text-white text-xs font-black uppercase">Available Wallet Balance</h5>
              <p className="text-zinc-400 text-[9px] font-medium mt-0.5">
                {isFirstTasteActive ? "Consuming complimentary signup tokens" : "Requires top-up or active BYOK configuration"}
              </p>
            </div>
            <span className="text-[#FFCC00] font-mono font-black text-xl" data-testid="payg-balance">
              {availableCredits} <span className="text-xs text-zinc-500">CR</span>
            </span>
          </div>
          <button
            type="button"
            data-testid="payg-topup"
            onClick={() => setShowPlans(true)}
            className="w-full h-11 bg-[#FFCC00] hover:bg-white text-black font-black text-xs tracking-wider uppercase rounded-xl flex items-center justify-center gap-2 transition-colors"
          >
            <CreditCard className="w-4 h-4" />
            {availableCredits === 0 ? "Buy Top-Up Credit Pack" : "Manage Plan · Buy Credits"}
          </button>
        </div>
      )}

      {/* Pro tier monthly inclusions (display-only caps) */}
      {isPro && (
        <div className="w-full bg-[#0A0A0A] border border-zinc-900 rounded-xl p-4 mb-4" data-testid="pro-inclusions">
          <div className="flex items-center justify-between mb-3">
            <span className="text-[10px] text-zinc-400 font-black tracking-widest uppercase">Monthly Asset Inclusions</span>
            <span className="text-[9px] bg-[#FFCC00]/10 text-[#FFCC00] border border-[#FFCC00]/30 px-2 py-0.5 rounded font-black tracking-wide uppercase">
              {user?.subscription_tier === "tier_3" ? "BUSINESS TIER" : "PRO CREATOR TIER"}
            </span>
          </div>
          <div className="grid grid-cols-3 gap-2.5">
            {INCLUSIONS.map((inc) => {
              const Icon = inc.Icon;
              return (
                <div key={inc.key} className="bg-black/40 border border-zinc-800 p-2.5 rounded-lg text-center" data-testid={`pro-inclusion-${inc.key}`}>
                  <Icon className="w-4 h-4 mx-auto mb-1" style={{ color: inc.color }} />
                  <span className="text-[10px] text-zinc-400 font-bold block mb-0.5">{inc.label}</span>
                  <span className="text-xs font-mono font-black">0/{inc.max}</span>
                </div>
              );
            })}
          </div>
          <p className="mt-2 text-[8px] text-zinc-600 font-bold tracking-tight text-center">Usage counters are display-only pending the live metering backend.</p>
        </div>
      )}

      {/* Director suite guard (illustrative) */}
      <div className="w-full bg-[#0A0A0A] border border-zinc-900 rounded-xl p-4 mb-4">
        <span className="text-[9px] text-zinc-400 font-black tracking-wider block mb-2 uppercase">Director Suite Integration Guard</span>
        <button
          type="button"
          data-testid="director-guard-btn"
          onClick={demoGuard}
          className="w-full h-11 bg-zinc-900 border border-zinc-800 hover:border-red-500 rounded-xl text-xs font-bold uppercase transition-all flex items-center justify-center gap-2"
        >
          <Sparkles className="w-3.5 h-3.5 text-red-400" />
          Test Director Layout Action
        </button>
        <div className="flex items-center gap-1.5 mt-2 justify-center text-[8px] text-zinc-500 font-bold tracking-tight text-center">
          <ShieldCheck className="w-3 h-3 text-emerald-600" />
          Atomic Transaction Guard: credits are only charged upon successful file completion.
        </div>
      </div>

      {/* Secure BYOK (OpenAI) */}
      <div className="w-full bg-[#0A0A0A] border border-zinc-900 rounded-xl p-4" data-testid="byok-openai-panel">
        <div className="flex items-center gap-2 mb-3">
          <Key className="w-4 h-4 text-[#FFCC00]" />
          <h4 className="text-white text-xs font-black tracking-widest uppercase">Bring Your Own Key (BYOK)</h4>
        </div>

        {isCasual && availableCredits === 0 && (
          <div className="bg-amber-950/20 border border-amber-900/50 p-3 rounded-lg flex items-start gap-2.5 mb-4">
            <ShieldAlert className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
            <p className="text-zinc-300 text-[10px] font-medium leading-relaxed">
              Your trial credits are exhausted. Paste a personal API key below to bypass workspace locks without processing package charges.
            </p>
          </div>
        )}

        {keySet && (
          <div className="flex items-center justify-between rounded-lg border border-zinc-800 bg-black/40 px-3 py-2 mb-3" data-testid="byok-openai-status">
            <span className="font-mono text-[11px] text-emerald-400">{keyMasked || "•••• configured"}</span>
            <button onClick={wipeKey} disabled={saving} data-testid="byok-openai-wipe" className="font-mono text-[9px] font-black uppercase tracking-widest text-red-400">Remove</button>
          </div>
        )}

        <div className="space-y-3">
          <div>
            <label className="text-zinc-400 text-[9px] font-black tracking-wider uppercase block mb-1">OpenAI API Endpoint Key</label>
            <div className="relative">
              <input
                data-testid="byok-openai-input"
                type={show ? "text" : "password"}
                value={keyInput}
                onChange={(e) => setKeyInput(e.target.value)}
                placeholder={keySet ? "Update OpenAI key…" : "sk-proj-........................"}
                className="w-full h-11 bg-black border border-zinc-800 rounded-lg px-3 pr-10 text-xs text-white tracking-widest focus:outline-none focus:border-[#FFCC00] transition-colors"
              />
              <button type="button" onClick={() => setShow((s) => !s)} data-testid="byok-openai-toggle" className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500">
                {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <button
            type="button"
            onClick={saveKey}
            disabled={saving}
            data-testid="byok-openai-save"
            className="w-full h-11 bg-zinc-900 border border-zinc-800 hover:border-[#FFCC00] text-white font-bold text-xs uppercase rounded-lg transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
          >
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
            {keySet ? "Update Cryptographic Key" : "Commit Cryptographic Key"}
          </button>
          <p className="text-[8px] leading-snug text-zinc-600">Stored encrypted at rest · used to run your AI pipelines under your own quota.</p>
        </div>
      </div>

      {showPlans && <PlansPanel onBack={() => setShowPlans(false)} />}
    </div>
  );
}
